import type { ComponentType, ReactNode } from 'react';
import { CheckCircle2, ClipboardList, FileText, Mail } from 'lucide-react';
import { useHasRequiredPolicyholderDocuments } from '@folio/hooks';
import { DocumentsListsPolicyModule, DocumentsUploadPolicyModule } from '@folio/modules';
import type { AfterSubmitResult } from 'korint-sdk/react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

interface AnonymousGeneratedDocumentsStepOptions {
  categories: string[];
  requiredDocuments: ReactNode[];
  /** Policyholder documents uploaded on this step; Next then waits for the required ones. */
  uploadDocuments?: { category: string; required?: boolean }[];
  beforeNext?: (params: {
    policyId: string;
    branchId: string;
  }) => AfterSubmitResult | Promise<AfterSubmitResult>;
  footer?: ReactNode;
  label?: string;
  icon?: ComponentType<{ className?: string }>;
}

function AnonymousGeneratedDocumentsAlertContent({
  requiredDocuments,
  footer,
}: {
  requiredDocuments: ReactNode[];
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Group gap={3} className="bg-gradient-to-r from-primary/15 to-primary/5 px-6 py-4">
        <Center className="h-10 w-10 shrink-0 rounded-full bg-primary/15">
          <CheckCircle2 className="h-5 w-5 text-primary" />
        </Center>
        <Title order={3} size="lg" tone="default">
          {t('anonymousGeneratedDocumentsStep.alertTitle')}
        </Title>
      </Group>
      <Stack className="px-6 py-5 text-sm leading-relaxed text-foreground/80 gap-5">
        <div>
          <Text weight="semibold" tone="default">
            {t('anonymousGeneratedDocumentsStep.documentsTitle')}
          </Text>
          <Stack className="mt-2 gap-1.5">
            <Group gap={2.5}>
              <FileText className="h-4 w-4 shrink-0 text-primary" />
              <span>{t('anonymousGeneratedDocumentsStep.pricingDocument')}</span>
            </Group>
            <Group gap={2.5}>
              <FileText className="h-4 w-4 shrink-0 text-primary" />
              <span>{t('anonymousGeneratedDocumentsStep.offerDocuments')}</span>
            </Group>
          </Stack>
        </div>
        <Group align="stretch" gap={3} className="rounded-lg bg-primary/5 p-3.5">
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p>{t('anonymousGeneratedDocumentsStep.emailNotice')}</p>
        </Group>
        <p>{t('anonymousGeneratedDocumentsStep.continueSubscription')}</p>
        <div>
          <Group gap={2} className="font-semibold text-foreground">
            <ClipboardList className="h-4 w-4 shrink-0 text-primary" />
            <span>{t('anonymousGeneratedDocumentsStep.requiredDocsTitle')}</span>
          </Group>
          <Stack asChild>
            <ul className="mt-2 ml-6 list-none pl-0 gap-1.5">
              {requiredDocuments.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                  {item}
                </li>
              ))}
            </ul>
          </Stack>
        </div>
        {footer}
      </Stack>
    </>
  );
}

function DocumentsListsModuleWrapper({ categories }: { categories: string[] }) {
  return (
    <DocumentsListsPolicyModule
      displayContext="policyholder"
      categories={categories}
      pollUntilAllDocumentsAreLoaded={true}
    />
  );
}

export function createAnonymousGeneratedDocumentsStep({
  categories,
  requiredDocuments,
  uploadDocuments,
  beforeNext,
  footer,
  label,
  icon,
}: AnonymousGeneratedDocumentsStepOptions) {
  return {
    path: 'generated-documents',
    type: 'module' as const,
    label,
    icon,
    get nextButtonText() {
      return i18next.t('anonymousGeneratedDocumentsStep.nextButtonText');
    },
    alert: {
      variant: 'info' as const,
      className: 'overflow-hidden rounded-xl p-0 block',
      description: (
        <AnonymousGeneratedDocumentsAlertContent
          requiredDocuments={requiredDocuments}
          footer={footer}
        />
      ),
    },
    module: uploadDocuments ? (
      <>
        <DocumentsUploadPolicyModule
          displayContext="policyholder"
          documentsLists={uploadDocuments.map(({ category, required = false }) => ({
            category,
            required,
          }))}
        />
        <DocumentsListsModuleWrapper categories={categories} />
      </>
    ) : (
      <DocumentsListsModuleWrapper categories={categories} />
    ),
    nextButton: uploadDocuments ? { useEnabled: useHasRequiredPolicyholderDocuments } : undefined,
    beforeNext,
  };
}
